<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header">
      <div class="section-number"><i class="fe fe-clock" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">Turnaround times</h6>
        <small style="color: rgba(255,255,255,.75);">
          Against the targets{{ data ? ` · ${data.period.from} to ${data.period.to}` : "" }}
        </small>
      </div>
      <router-link v-if="compact" to="/authorization-code-statistics" class="btn btn-sm btn-light">Details</router-link>
    </div>
    <div class="card-body">
      <div v-if="loading" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
      <div v-else-if="error" class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div>
      <template v-else-if="data">
        <div class="row">
          <div class="col-md-3 col-6" v-for="tile in tiles" :key="tile.label">
            <div class="stat-card">
              <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
              <div>
                <p class="stat-label">{{ tile.label }}</p>
                <h3 class="stat-value">{{ tile.value }}</h3>
                <p class="stat-sub">{{ tile.sub }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table mb-0">
            <thead class="table-head">
              <tr>
                <th class="table-th">Stage</th>
                <th class="table-th">Target</th>
                <th class="table-th">Decided</th>
                <th class="table-th">Average</th>
                <th class="table-th">Median</th>
                <th class="table-th">Within target</th>
                <th class="table-th">Waiting now</th>
                <th class="table-th">Overdue now</th>
              </tr>
            </thead>
            <tbody>
              <tr class="table-row" v-for="row in stages" :key="row.label">
                <td><b>{{ row.label }}</b></td>
                <td>{{ fmt(row.s.target, row.s.unit) }}</td>
                <td>{{ row.s.decided }}</td>
                <td>{{ row.s.average === null ? "—" : fmt(row.s.average, row.s.unit) }}</td>
                <td>{{ row.s.median === null ? "—" : fmt(row.s.median, row.s.unit) }}</td>
                <td>
                  <span v-if="row.s.within_target_pct === null" class="muted">—</span>
                  <span v-else :class="row.s.within_target_pct >= 80 ? 'delta-down' : 'delta-up'">{{ row.s.within_target_pct }}%</span>
                </td>
                <td>{{ row.s.pending_now }}</td>
                <td><span :class="row.s.overdue_now ? 'sev sev-high' : 'muted'">{{ row.s.overdue_now }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <template v-if="!compact">
          <h6 class="font-weight-bold mt-4 mb-2">Overdue authorization code requests</h6>
          <div v-if="!codes.overdue.length" class="text-muted small">Nothing overdue.</div>
          <div class="table-responsive" v-else>
            <table class="table mb-0">
              <thead class="table-head">
                <tr><th class="table-th">Requested</th><th class="table-th">Patient</th><th class="table-th">Facility</th><th class="table-th">Waiting</th><th class="table-th">Escalated</th><th class="table-th"></th></tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="c in codes.overdue" :key="c.id">
                  <td>{{ c.requested_at }}</td>
                  <td>{{ c.patient || "—" }} <span v-if="c.is_corridor" class="sev sev-info ml-1">Cross-state</span></td>
                  <td>{{ c.facility || "—" }}</td>
                  <td><span class="delta-up">{{ fmt(c.waiting_minutes, "minutes") }}</span></td>
                  <td>{{ c.escalation_level ? (c.escalation_level === 2 ? "Admins" : "Reviewers") : "—" }}</td>
                  <td><router-link :to="`/authorization-code/${c.id}`" class="btn btn-sm btn-outline-primary">Open</router-link></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="font-weight-bold mt-4 mb-2">Decisions by staff</h6>
          <div v-if="!codes.by_staff.length" class="text-muted small">No decisions in this period.</div>
          <div class="table-responsive" v-else>
            <table class="table mb-0">
              <thead class="table-head">
                <tr><th class="table-th">Staff</th><th class="table-th">Decisions</th><th class="table-th">Average time</th><th class="table-th">Within target</th></tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="p in codes.by_staff" :key="p.user_id">
                  <td>{{ p.name }}</td>
                  <td>{{ p.decisions }}</td>
                  <td>{{ fmt(p.avg_minutes, "minutes") }}</td>
                  <td>{{ p.within_target_pct === null ? "—" : p.within_target_pct + "%" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="font-weight-bold mt-4 mb-2">Overdue claims</h6>
          <div v-if="!overdueClaims.length" class="text-muted small">Nothing overdue.</div>
          <div class="table-responsive" v-else>
            <table class="table mb-0">
              <thead class="table-head">
                <tr><th class="table-th">Claim</th><th class="table-th">Stage</th><th class="table-th">Facility</th><th class="table-th">Waiting</th><th class="table-th"></th></tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="c in overdueClaims" :key="c.stage + c.id">
                  <td>{{ c.claim }}</td>
                  <td>{{ c.stage }}</td>
                  <td>{{ c.facility || "—" }}</td>
                  <td><span class="delta-up">{{ c.waiting_days }} days</span></td>
                  <td><router-link :to="`/claim/${c.id}`" class="btn btn-sm btn-outline-primary">Open</router-link></td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * Turnaround times of authorization codes and claims against the agency's targets
 * (GET turnaround/summary). compact = the dashboard version (stage table only).
 */
export default {
  name: "TurnaroundPanel",
  props: {
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    compact: { type: Boolean, default: false },
  },
  data() {
    return { data: null, loading: false, error: "" };
  },
  computed: {
    codes() {
      return this.data.authorization_codes;
    },
    stages() {
      const c = this.data.claims;
      return [
        { label: "Authorization codes (request → decision)", s: this.codes },
        { label: "Claims: submitted → vetted", s: c.vetting },
        { label: "Claims: vetted → verified", s: c.verification },
        { label: "Claims: verified → approved", s: c.approval },
      ];
    },
    tiles() {
      const c = this.codes;
      const claimsOverdue = ["vetting", "verification", "approval"].reduce((n, k) => n + this.data.claims[k].overdue_now, 0);
      return [
        { label: "Codes overdue now", value: c.overdue_now, sub: `${c.approaching_now} approaching the ${c.target} min target`, icon: "fe fe-alert-octagon", bg: "#fef2f2", color: "#dc2626" },
        { label: "Codes decided in time", value: c.within_target_pct === null ? "—" : c.within_target_pct + "%", sub: `${c.decided} decisions in the period`, icon: "fe fe-check-circle", bg: "#ecfdf5", color: "#059669" },
        { label: "Median code decision", value: c.median === null ? "—" : this.fmt(c.median, "minutes"), sub: `Average ${c.average === null ? "—" : this.fmt(c.average, "minutes")}`, icon: "fe fe-clock", bg: "#eff6ff", color: "#2563eb" },
        { label: "Claims overdue now", value: claimsOverdue, sub: "Across vetting, verification, approval", icon: "fe fe-file-text", bg: "#fff7ed", color: "#ea580c" },
      ];
    },
    overdueClaims() {
      const names = { vetting: "Vetting", verification: "Verification", approval: "Approval" };
      return Object.keys(names).flatMap((k) => this.data.claims[k].overdue.map((c) => ({ ...c, stage: names[k] })));
    },
  },
  watch: {
    from() { this.load(); },
    to() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      this.axios
        .get("/api/v1/auth/turnaround/summary", { params: { from: this.from || undefined, to: this.to || undefined } })
        .then((res) => { this.data = res.data.data; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load turnaround times."; })
        .finally(() => { this.loading = false; });
    },
    fmt(value, unit) {
      if (value === null || value === undefined) return "—";
      if (unit === "days") return `${value} day${value == 1 ? "" : "s"}`;
      const m = Number(value);
      if (m < 60) return `${Math.round(m)} min`;
      if (m < 60 * 48) return `${(m / 60).toFixed(1)} h`;
      return `${(m / 1440).toFixed(1)} days`;
    },
  },
};
</script>

<style scoped>
@import "../reports.css";
</style>
